import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import { MathService } from '../../../services/math-service';
import { ItemVisibilityService } from '../../../services/item-visibility.service';
import { ItemSearchService } from '../../services/item-search.service';
import { ScanDetails } from '../../layout/scan-details';
import { warningTypes } from '../../constants';
import { ItemList } from '../../item-list/scan-to-verify-items';
import { VerifySection } from '../verify-details';
import { VerifyHeader } from '../verify-header';
import { VerifyFooter } from '../verify-footer';
import { ScanToPrintFulfillmentPlanner } from '../../scan-to-print-fulfillment-planner/';
import { VerificationNotifier } from '../verification-notifier';
import { getOrdersForOrderNumberPromise } from '../../../store/scan-to-print/scan-to-print.api';
import SuccessSound from './sounds/success.mp3';
import ErrorSound from './sounds/error.mp3';
import VerificationSound from './sounds/verification_success.mp3';
import { InvalidScanWarningModal } from '../invalid-scan-warning-modal';
import { useModal } from '../../../hooks/use-modal';
import { trackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { noop } from 'ui-components/default-props/noop';
import { ScanBodyHeader } from '../../body-header';
import { TitleBox } from '../../layout/title-box';

const isUnverifiedProductCode = (verifiedItemCounts, matchedItemId) => {
  return (item) => {
    const verifiedItemCount = verifiedItemCounts[item.itemId] || 0;
    return item.itemId === matchedItemId && item.itemQuantity > verifiedItemCount;
  };
};

export class ScanToVerifyBody extends React.Component {
  static propTypes = {
    fulfillmentPlanId: PropTypes.string,
    items: PropTypes.array,
    isVerified: PropTypes.bool,
    itemPreference: PropTypes.array.isRequired,
    automaticConfirmation: PropTypes.bool,
    submitVerification: PropTypes.func.isRequired,
    hasMarketPlaceAdjustments: PropTypes.bool.isRequired,
    itemAdjustments: PropTypes.array.isRequired,
    marketplaceId: PropTypes.string.isRequired,
    setScanHeaderFocus: PropTypes.func.isRequired,
    isVerifyOnly: PropTypes.bool.isRequired,
    focusGlobalEventListenerRoot: PropTypes.func.isRequired,
  };

  static defaultProps = {
    items: [],
    isVerified: false,
    automaticConfirmation: false,
  };

  constructor(...args) {
    super(...args);
    this.state = {
      verifiedItemCounts: {},
      currentlyVerifyingItemId: '',
      showVerificationNotification: false,
      verificationNotifcationItemId: '',
      editVerificationClicked: false,
    };
    this.successAudio = new Audio(SuccessSound);
    this.failureAudio = new Audio(ErrorSound);
    this.verifiedAudio = new Audio(VerificationSound);
    this.warning = undefined;
    this.debouncedSubmitVerification = null;
  }

  componentWillUnmount() {
    this.clearTimeouts();

    if (this.debouncedSubmitVerification) {
      this.debouncedSubmitVerification.flush();
    }
  }

  onVerifyItem = (itemId, numVerified, isBulkVerify = false) => {
    const itemIsVisible = ItemVisibilityService.isItemVisible(itemId);
    if (!isBulkVerify) {
      this.updateVerifiedItemCountsAndCheckToSubmit({ [itemId]: numVerified });
    }
    this.setState(() => ({
      currentlyVerifyingItemId: itemId,
      verificationNotifcationItemId: itemId,
      showVerificationNotification: !itemIsVisible && !isBulkVerify,
      editVerificationClicked: false,
    }));

    this.clearTimeouts();
    this.rowFlashTimeout = setTimeout(
      () =>
        this.setState({
          currentlyVerifyingItemId: '',
        }),
      1000,
    );
    this.notificationTimeout = setTimeout(
      () =>
        this.setState({
          showVerificationNotification: false,
          verificationNotifcationItemId: '',
        }),
      3000,
    );
    this.audioTimeout = setTimeout(() => this.playGoodAudio(), 100);
  };

  onClearItem = (itemId) => {
    this.setState((state) => ({
      verifiedItemCounts: {
        ...state.verifiedItemCounts,
        [itemId]: 0,
      },
      currentlyVerifyingItemId: '',
    }));
  };

  onBadScan = (openInvalidScanWarningModal) => {
    openInvalidScanWarningModal();
    this.failureAudio.load();
    this.failureAudio.play().catch(noop);
  };

  onProductCodeEnter = async (productCode, openInvalidScanWarningModal) => {
    if (!productCode?.length) {
      return;
    }
    const { items, itemPreference } = this.props;
    const { verifiedItemCounts } = this.state;
    const itemExistsInFP = ItemSearchService.findItemInFP(productCode, this.getItems(), itemPreference);
    if (!itemExistsInFP) {
      try {
        // just in case, lets see if the number is a order
        const orderData = await getOrdersForOrderNumberPromise(productCode);
        if (orderData.length) {
          this.warning = warningTypes.scannedOrderNumber;
          return this.onBadScan(openInvalidScanWarningModal);
        }
        this.warning = warningTypes.incorrectProduct;
        return this.onBadScan(openInvalidScanWarningModal);
      } catch (e) {
        if (e?.name === 'AbortError') {
          // do nothing
          return;
        }
        this.warning = warningTypes.incorrectProduct;
        return this.onBadScan(openInvalidScanWarningModal);
      }
    }
    const foundItem = items.find(isUnverifiedProductCode(verifiedItemCounts, itemExistsInFP.itemId));
    if (!foundItem) {
      this.warning = warningTypes.extraProduct;
      return this.onBadScan(openInvalidScanWarningModal);
    }
    const foundItemId = foundItem.itemId;
    const currItemVerifiedCount = verifiedItemCounts[foundItemId] || 0;
    const numVerified = MathService.clamp(currItemVerifiedCount + 1, 0, foundItem.itemQuantity);

    trackWorkflow(Workflows.SCAN_TO, WorkflowSteps.ITEM_VERIFIED_SCANNED, {
      itemId: foundItemId,
      verifiedCount: numVerified,
    });

    this.onVerifyItem(foundItemId, numVerified);
  };

  onClearAllItems = () => {
    const { focusGlobalEventListenerRoot, items } = this.props;
    items.forEach((item) => this.onClearItem(item.itemId));

    trackWorkflow(Workflows.SCAN_TO, WorkflowSteps.CLEAR_ALL, {
      itemCount: items.length,
    });

    focusGlobalEventListenerRoot();
  };

  onVerifyAllItems = () => {
    const { focusGlobalEventListenerRoot, items } = this.props;
    items.forEach((item) => this.onVerifyItem(item.itemId, item.itemQuantity, true));
    const stateUpdate = items.reduce(
      (acc, item) => ({
        ...acc,
        [item.itemId]: item.itemQuantity,
      }),
      {},
    );
    this.updateVerifiedItemCountsAndCheckToSubmit(stateUpdate);

    trackWorkflow(Workflows.SCAN_TO, WorkflowSteps.VERIFY_ALL, {
      itemCount: items.length,
      totalQuantity: this.getTotalItemCount(),
    });

    focusGlobalEventListenerRoot();
  };

  onEditVerificationClick = () => {
    const { items } = this.props;
    const verifiedItemCounts = items.reduce((acc, item) => {
      return {
        ...acc,
        [item.itemId]: item.itemQuantity,
      };
    }, {});
    this.setState({
      verifiedItemCounts,
      editVerificationClicked: true,
    });

    trackWorkflow(Workflows.SCAN_TO, WorkflowSteps.EDIT_VERIFICATION, {
      verifiedCounts: verifiedItemCounts,
    });
  };

  getItemVerificationCount = (itemId) => {
    const { verifiedItemCounts } = this.state;
    return verifiedItemCounts[itemId] || 0;
  };

  getTotalItemCount = () => {
    const { items } = this.props;
    return items.reduce((acc, item) => acc + item.itemQuantity, 0);
  };

  getVerifiedItemCount = () => {
    const { items, isVerified } = this.props;
    if (isVerified) {
      return this.getTotalItemCount();
    }
    return items.reduce((acc, item) => acc + this.getItemVerificationCount(item.itemId), 0);
  };

  getItems = () => {
    const { items, isVerified } = this.props;
    return items.map((item) => {
      const currentCount = this.getItemVerificationCount(item.itemId);
      if ((isVerified || item.itemQuantity === currentCount) && !item.hasMarketplaceAdjustment) {
        return {
          ...item,
          verified: true,
        };
      }
      return {
        ...item,
        verified: false,
        verifiedQuantity: currentCount,
      };
    });
  };

  updateVerifiedItemCountsAndCheckToSubmit = (verifiedItemCountsUpdate) => {
    this.setState(
      (state) => ({
        verifiedItemCounts: {
          ...state.verifiedItemCounts,
          ...verifiedItemCountsUpdate,
        },
      }),
      this.confirmOrderAutomatically,
    );
  };

  confirmOrderAutomatically = () => {
    if (!this.props.automaticConfirmation) {
      return;
    }
    const { verifiedItemCounts, editVerificationClicked } = this.state;
    const totalItemCount = this.getTotalItemCount();
    const verifiedCount = this.props.items.reduce((acc, item) => {
      const itemCount = verifiedItemCounts[item.itemId] || 0;
      return acc + itemCount;
    }, 0);
    const { focusGlobalEventListenerRoot, isVerifyOnly, submitVerification, setScanHeaderFocus } = this.props;
    if (totalItemCount === verifiedCount && !editVerificationClicked) {
      if (this.debouncedSubmitVerification) {
        this.debouncedSubmitVerification.cancel();
      }
      this.debouncedSubmitVerification = debounce(() => {
        submitVerification();
        this.debouncedSubmitVerification = null;

        if (isVerifyOnly) {
          trackWorkflow(Workflows.SCAN_TO, WorkflowSteps.COMPLETE, {
            totalItemCount,
            verifiedCount,
          });
        }
      }, 100);
      this.debouncedSubmitVerification();
      focusGlobalEventListenerRoot();
      if (isVerifyOnly) {
        setScanHeaderFocus(false);
      }
    }
  };

  areAllItemsVerified = () => {
    return this.getTotalItemCount() === this.getVerifiedItemCount();
  };

  playGoodAudio = () => {
    if (this.areAllItemsVerified()) {
      this.verifiedAudio.load();
      this.verifiedAudio.play().catch(noop);
    } else {
      this.successAudio.load();
      this.successAudio.play().catch(noop);
    }
  };

  clearTimeouts = () => {
    if (this.notificationTimeout) clearTimeout(this.notificationTimeout);
    if (this.audioTimeout) clearTimeout(this.audioTimeout);
    if (this.rowFlashTimeout) clearTimeout(this.rowFlashTimeout);
  };

  renderItems = () => {
    return (
      <ItemList
        currentlyVerifyingItemId={this.state.currentlyVerifyingItemId}
        items={this.getItems()}
        marketplaceId={this.props.marketplaceId}
        onClearAllItems={this.onClearAllItems}
        onClearItem={this.onClearItem}
        onVerifyAllItems={this.onVerifyAllItems}
        onVerifyItem={this.onVerifyItem}
        renderItemVerificationNotification={this.renderItemVerificationNotification}
      />
    );
  };

  renderHeader = (openInvalidScanWarningModal, isInvalidScanWarningModalOpen) => {
    const { isVerified, hasMarketPlaceAdjustments, marketplaceId, itemAdjustments } = this.props;
    return (
      <VerifyHeader
        allItemsVerified={this.areAllItemsVerified()}
        hasMarketPlaceAdjustments={hasMarketPlaceAdjustments}
        isInvalidScanWarningModalOpen={isInvalidScanWarningModalOpen}
        isVerified={isVerified}
        itemAdjustments={itemAdjustments}
        marketplaceId={marketplaceId}
        onProductCodeEnter={this.onProductCodeEnter}
        openInvalidScanWarningModal={openInvalidScanWarningModal}
      />
    );
  };

  renderFooter = () => {
    const showAutomaticFooter = !this.state.editVerificationClicked && this.props.automaticConfirmation;
    return (
      <VerifyFooter
        allItemsVerified={this.areAllItemsVerified()}
        automaticConfirmation={showAutomaticFooter}
        isVerified={this.props.isVerified}
        onEditVerificationClick={this.onEditVerificationClick}
        totalItemCount={this.getTotalItemCount()}
        verifiedItemCount={this.getVerifiedItemCount()}
      />
    );
  };

  renderItemVerificationNotification = () => {
    const { showVerificationNotification, verificationNotifcationItemId } = this.state;
    const item = this.props.items.find((x) => x.itemId === verificationNotifcationItemId);
    if (showVerificationNotification && item) {
      const { name = 'Scanned Item' } = item;
      const close = () => {
        this.setState({ showVerificationNotification: false });
      };
      return <VerificationNotifier closeNotification={close} name={name} />;
    }
    return null;
  };

  renderLeft = () => {
    const { isVerified, fulfillmentPlanId } = this.props;
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const { open: openInvalidScanWarningModal, isOpen: isInvalidScanWarningModalOpen, close: closeInvalidScanWarningModal } = useModal();
    return (
      <TitleBox
        renderBody={() => (
          <>
            {isInvalidScanWarningModalOpen && (
              <InvalidScanWarningModal closeModal={closeInvalidScanWarningModal} warningType={this.warning} />
            )}
            <VerifySection
              disabled={isVerified}
              isInvalidScanWarningModalOpen={isInvalidScanWarningModalOpen}
              openInvalidScanWarningModal={openInvalidScanWarningModal}
              renderFooter={this.renderFooter}
              renderHeader={this.renderHeader}
              renderItems={this.renderItems}
            />
          </>
        )}
        renderTitle={() => <ScanBodyHeader shipmentNumber={fulfillmentPlanId} />}
      />
    );
  };

  render() {
    const { isVerifyOnly } = this.props;
    return <ScanDetails renderLeft={this.renderLeft} renderRight={isVerifyOnly ? undefined : () => <ScanToPrintFulfillmentPlanner />} />;
  }
}
